import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { logger } from '../utils/logger';

export interface BackgroundTaskNotification {
  id: string;
  type: 'xero_sync' | 'xero_webhook' | 'myob_sync' | 'data_import' | 'export' | 'integration_sync';
  title: string;
  message: string;
  status: 'in_progress' | 'completed' | 'error';
  progress?: number;
  timestamp: string;
  autoHide?: boolean;
  autoHideDelay?: number;
  link?: string;
  linkText?: string;
}

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  notifications: BackgroundTaskNotification[];
  dismissNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<BackgroundTaskNotification[]>([]);
  const autoHideTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const currentUserIdRef = useRef<string>('');
  const conversationsRef = useRef<Map<string, { unreadCount: number; lastMessageAt: string }>>(new Map());
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Initialize socket connection
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return;
    }
    
    // Initialize conversation state by fetching current unread counts
    const initializeConversations = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${apiUrl}/api/messages/conversations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          // Initialize conversation ref with current state
          data.data.forEach((conv: any) => {
            conversationsRef.current.set(conv.id, {
              unreadCount: conv.unreadCount || 0,
              lastMessageAt: conv.lastMessageAt || new Date().toISOString(),
            });
          });
          
          // Calculate and emit initial total unread count
          let totalUnread = 0;
          conversationsRef.current.forEach((conv) => {
            totalUnread += conv.unreadCount;
          });
          
          window.dispatchEvent(
            new CustomEvent('unreadMessagesUpdate', { detail: totalUnread })
          );
        }
      } catch (error) {
        console.error('Failed to initialize conversations:', error);
      }
    };
    
    const newSocket = io(apiUrl, {
      auth: {
        token
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      // Initialize conversations when socket connects
      initializeConversations();
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      logger.error('WebSocket connection error:', error.message);
      setIsConnected(false);
    });

    // Listen for task notifications
    newSocket.on('task:notification', (notification: BackgroundTaskNotification) => {
      setNotifications(prev => {
        // Replace existing notification with same ID, or add new one
        const existing = prev.findIndex(n => n.id === notification.id);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = notification;
          return updated;
        }
        return [notification, ...prev];
      });

      // Setup auto-hide timer if specified
      if (notification.autoHide && notification.autoHideDelay) {
        // Clear any existing timer for this notification
        const existingTimer = autoHideTimers.current.get(notification.id);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        // Set new timer
        const timer = setTimeout(() => {
          dismissNotification(notification.id);
        }, notification.autoHideDelay);
        
        autoHideTimers.current.set(notification.id, timer);
      }
    });

    // Listen for task progress updates
    newSocket.on('task:progress', (data: { taskId: string; progress: number; message: string; timestamp: string }) => {
      setNotifications(prev => {
        const taskNotification = prev.find(n => n.id.startsWith(data.taskId));
        if (taskNotification) {
          return prev.map(n => 
            n.id.startsWith(data.taskId)
              ? { ...n, progress: data.progress, message: data.message, timestamp: data.timestamp }
              : n
          );
        }
        return prev;
      });
    });

    // Listen for task completion
    newSocket.on('task:complete', (data: { taskId: string; message: string; success: boolean; timestamp: string }) => {
      setNotifications(prev => {
        // Try to find and update existing notification
        const existingIndex = prev.findIndex(n => n.id.startsWith(data.taskId));
        
        if (existingIndex >= 0) {
          // Update existing notification
          return prev.map(n => 
            n.id.startsWith(data.taskId)
              ? { 
                  ...n, 
                  status: data.success ? 'completed' as const : 'error' as const,
                  message: data.message,
                  timestamp: data.timestamp,
                  progress: data.success ? 100 : n.progress
                }
              : n
          );
        } else {
          // Create new notification if one doesn't exist (for suppressed progress notifications)
          const newNotification: BackgroundTaskNotification = {
            id: `${data.taskId}-complete`,
            type: 'integration_sync',
            title: data.success ? 'Task Completed' : 'Task Failed',
            message: data.message,
            status: data.success ? 'completed' : 'error',
            progress: data.success ? 100 : undefined,
            timestamp: data.timestamp,
            autoHide: true,
            autoHideDelay: 5000
          };
          
          // Setup auto-hide timer
          const timer = setTimeout(() => {
            dismissNotification(newNotification.id);
          }, newNotification.autoHideDelay);
          autoHideTimers.current.set(newNotification.id, timer);
          
          return [newNotification, ...prev];
        }
      });
    });

    // Fetch current user ID for message filtering
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${apiUrl}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.success) {
          currentUserIdRef.current = data.user.id;
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    };

    fetchCurrentUser();

    // Persistent message listener - works even when MessagingPane is closed
    // This ensures unread counts and conversation updates happen in real-time
    const handleNewMessage = (data: {
      conversationId: string;
      message: {
        _id: string;
        senderId: { _id: string };
        content: string;
        createdAt: string;
      };
      timestamp: string;
    }) => {
      const isFromOtherUser = data.message.senderId._id !== currentUserIdRef.current;
      
      // Update conversation state
      const existing = conversationsRef.current.get(data.conversationId);
      const currentUnreadCount = existing?.unreadCount || 0;
      
      // Increment unread count if message is from another user
      // Note: We don't know if conversation is currently open, so we increment
      // The MessagingPane will handle decrementing when it's opened
      const newUnreadCount = isFromOtherUser 
        ? currentUnreadCount + 1 
        : currentUnreadCount;
      
      conversationsRef.current.set(data.conversationId, {
        unreadCount: newUnreadCount,
        lastMessageAt: data.timestamp,
      });

      // Calculate total unread across all conversations
      let totalUnread = 0;
      conversationsRef.current.forEach((conv) => {
        totalUnread += conv.unreadCount;
      });

      // Emit custom event for RightSidebar and MessagingPane
      window.dispatchEvent(
        new CustomEvent('unreadMessagesUpdate', { detail: totalUnread })
      );

      // Emit conversation update event for MessagingPane to sync
      window.dispatchEvent(
        new CustomEvent('conversationMessageUpdate', {
          detail: {
            conversationId: data.conversationId,
            message: data.message,
            timestamp: data.timestamp,
            unreadCount: newUnreadCount,
          },
        })
      );
    };

    const handleConversationUpdate = (data: {
      conversationId: string;
      update: any;
      timestamp: string;
    }) => {
      // Update conversation metadata
      const existing = conversationsRef.current.get(data.conversationId);
      conversationsRef.current.set(data.conversationId, {
        unreadCount: existing?.unreadCount || 0,
        lastMessageAt: data.update.lastMessageAt || data.timestamp,
      });

      // Emit update event for MessagingPane
      window.dispatchEvent(
        new CustomEvent('conversationMetadataUpdate', {
          detail: {
            conversationId: data.conversationId,
            update: data.update,
            timestamp: data.timestamp,
          },
        })
      );
    };

    // Set up persistent listeners
    newSocket.on('message:new', handleNewMessage);
    newSocket.on('conversation:update', handleConversationUpdate);

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      // Clear all auto-hide timers
      autoHideTimers.current.forEach(timer => clearTimeout(timer));
      autoHideTimers.current.clear();
      
      // Remove message listeners
      newSocket.off('message:new', handleNewMessage);
      newSocket.off('conversation:update', handleConversationUpdate);
      
      newSocket.close();
    };
    // Empty deps - only run once per mount/unmount cycle
    // React StrictMode will cause double-mount in dev (expected behavior)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissNotification = useCallback((id: string) => {
    // Clear timer if exists
    const timer = autoHideTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      autoHideTimers.current.delete(id);
    }
    
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    // Clear all timers
    autoHideTimers.current.forEach(timer => clearTimeout(timer));
    autoHideTimers.current.clear();
    
    setNotifications([]);
  }, []);

  const value: WebSocketContextType = {
    socket,
    isConnected,
    notifications,
    dismissNotification,
    clearAllNotifications
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

